import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ConfirmationNumber as TicketIcon,
} from '@mui/icons-material';
import { Alert, RootState, TicketPriority } from '../types';
import { useLayout } from '../components/contexts/LayoutContext';
import NewTicketForm from '../components/NewTicketForm';
// import CreateAlertDialog from '../components/CreateAlertDialog';
import { useSelector, useDispatch } from 'react-redux';
import {fetchAlerts, setSelectedAlert, clearSelectedAlert, fetchAlertById} from '../store/slices/alertsSlice';
import { AppDispatch } from '../store';
import { DataTable, Column } from '../components/common';
import { colorUtils } from '../theme';
import {getSeverityChipSx, getStatusChipSx, getStatusColor} from "../theme/mappers/ticketMappers";
import { formatDate } from '../utils/dateUtils';


const AlertsPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { alerts, isLoading, isDetailsLoading, error, selectedAlert } = useSelector((state: RootState) => state.alerts);

  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [contextMenuAlert, setContextMenuAlert] = useState<Alert | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [alertForTicket, setAlertForTicket] = useState<Alert | null>(null);

  // Responsive breakpoints
  const isDetailsOpen = !!selectedAlert;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarCollapsed } = useLayout();

  // Determine which columns to show based on available space
  const showAllColumns = !isDetailsOpen && !isSmallScreen && sidebarCollapsed;
  const showMediumColumns = !isDetailsOpen || !isVerySmallScreen;

  // Fetch alerts on mount
  useEffect(() => {
    dispatch(fetchAlerts());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearSelectedAlert());
    };
  }, [dispatch]);

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return theme.palette.severity.critical;
      case 'high': return theme.palette.severity.high;
      case 'medium': return theme.palette.severity.medium;
      case 'low': return theme.palette.severity.low;
      default: return theme.palette.severity.low;
    }
  };

  const normalizeSeverity = (severity: string) => {
    const s = severity?.toLowerCase() || 'low';
    if (['critical', 'high', 'medium', 'low'].includes(s)) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    return 'Low';
  };


  // Derive unique status values for the Status dropdown
  const alertStatuses = useMemo(() => {
    if (!Array.isArray(alerts)) return [];
    const statuses = alerts
      .map(a => a.status)
      .filter((s): s is string => !!s);
    return Array.from(new Set(statuses)).sort();
  }, [alerts]);

  // Derive unique integration_type values for the Category dropdown
  const integrationTypes = useMemo(() => {
    if (!Array.isArray(alerts)) return [];
    const types = alerts
      .map(a => a.integration_type)
      .filter((t): t is string => !!t);
    return Array.from(new Set(types)).sort();
  }, [alerts]);

  // Filter alerts client-side.
  // TODO(backend-filters): when the API supports filter params, pass
  // severityFilter and categoryFilter as query args to fetchAlerts and
  // remove the matchesSeverity / matchesCategory checks below.
  const filteredAlerts = useMemo(() => {
    if (!Array.isArray(alerts)) {
      console.warn('Alerts is not an array:', alerts);
      return [];
    }

    return alerts.filter(alert => {
      const matchesSearch = !searchTerm || (
        alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (alert.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (alert.integration_type?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        alert.client_id.toString().includes(searchTerm)
      );
      const matchesSeverity = !severityFilter || normalizeSeverity(alert.severity) === severityFilter;
      const matchesCategory = !categoryFilter || (alert.integration_type || '') === categoryFilter;
      const matchesStatus = !statusFilter || (alert.status || 'new') === statusFilter;

      return matchesSearch && matchesSeverity && matchesCategory && matchesStatus;
    });
  }, [alerts, searchTerm, severityFilter, categoryFilter]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleRefresh = () => {
    dispatch(fetchAlerts());
  };

  const handleRowClick = (alert: Alert) => {
    dispatch(fetchAlertById(alert.id));
  };

  const mapSeverityToPriority = (severity: string): TicketPriority => {
    switch (severity?.toLowerCase()) {
      case 'critical': return TicketPriority.CRITICAL;
      case 'high': return TicketPriority.HIGH;
      case 'medium': return TicketPriority.MEDIUM;
      case 'low': return TicketPriority.LOW;
      default: return TicketPriority.MEDIUM;
    }
  };

  const handleRowContextMenu = (alert: Alert, event: React.MouseEvent) => {
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
    setContextMenuAlert(alert);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setContextMenuAlert(null);
  };

  const handleCreateTicketFromContext = () => {
    if (contextMenuAlert) {
      setAlertForTicket(contextMenuAlert);
      setCreateDialogOpen(true);
    }
    handleContextMenuClose();
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setAlertForTicket(null);
  };

  // Define table columns with priority levels
  // Priority: 1 = always show, 2 = show when medium space, 3 = show only with full space
  const allColumns: (Column<Alert> & { priority: number })[] = [
      // {
      //     key: 'id',
      //     header: 'ID',
      //     minWidth: 200,
      //     maxWidth: 400,
      //     priority: 1,
      //     render: (alert) => (
      //         <Typography
      //             variant="body2"
      //             sx={{
      //                 textAlign: 'center',
      //                 fontWeight: 500,
      //                 overflow: 'hidden',
      //                 textOverflow: 'ellipsis',
      //                 whiteSpace: 'nowrap',
      //             }}
      //         >
      //             {alert.id}
      //         </Typography>
      //     ),
      // },
      {
          key: 'hostname',
          header: 'Hostname',
          minWidth: 130,
          maxWidth: 200,
          align: 'left',
          priority: 1,
          sortable: true,
          sortValue: (alert) => alert.hostname || '',
          render: (alert) => (
              <Typography
                  variant="body2"
                  sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                  }}
              >
                  {alert.hostname}
              </Typography>
          ),
      },
      {
          key: 'username',
          header: 'Username',
          minWidth: 120,
          maxWidth: 180,
          align: 'left',
          priority: 1,
          sortable: true,
          sortValue: (alert) => alert.username || '',
          render: (alert) => (
              <Typography
                  variant="body2"
                  sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                  }}
              >
                  {alert.username}
              </Typography>
          ),
      },
      {
          key: 'client_name',
          header: 'Client',
          minWidth: 120,
          maxWidth: 180,
          align: 'left',
          priority: 1,
          sortable: true,
          sortValue: (alert) => alert.client_name || '',
          render: (alert) => (
              <Typography
                  variant="body2"
                  sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                  }}
              >
                  {alert.client_name}
              </Typography>
          ),
      },
      {
          key: 'filename_ipaddress',
          header: 'File / IP',
          minWidth: 140,
          maxWidth: 220,
          align: 'left',
          priority: 1,
          render: (alert) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                  <Typography
                      variant="body2"
                      sx={{
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                      }}
                  >
                      {alert.file_name}
                  </Typography>
                  <Typography
                      variant="body2"
                      sx={{
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                      }}
                  >
                      {alert.ip_address}
                  </Typography>
              </Box>
          ),
      },
      {
          key: 'severity',
          header: 'Severity',
          minWidth: 100,
          align: 'center',
          priority: 1,
          sortable: true,
          sortValue: (alert) => {
            const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
            return order[alert.severity?.toLowerCase()] ?? 4;
          },
          render: (alert) => (
              <Chip
                  label={normalizeSeverity(alert.severity)}
                  size="small"
                  sx={getSeverityChipSx(getSeverityColor(alert.severity))}
              />
          ),
      },
    {
      key: 'title',
      header: 'Alert Title',
      minWidth: 180,
      maxWidth: 320,
      align: 'left',
      priority: 1,
      sortable: true,
      sortValue: (alert) => alert.title || '',
      render: (alert) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {alert.title}
        </Typography>
      ),
    },
      {
          key: 'mitre_tactic',
          header: 'MITRE',
          minWidth: 140,
          maxWidth: 200,
          align: 'left',
          priority: 1,
          render: (alert) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                  <Typography
                      variant="body2"
                      sx={{
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                      }}
                  >
                      {alert.mitre_tactic}
                  </Typography>
                  <Typography
                      variant="body2"
                      sx={{
                          fontSize: '0.75rem',
                          color: theme.palette.text.secondary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                      }}
                  >
                      {alert.mitre_technique}
                  </Typography>
              </Box>
          ),
      },
    // {
    //   key: 'action_taken',
    //   header: 'Action Taken',
    //   minWidth: 130,
    //   noWrap: true,
    //   align: 'left',
    //   priority: 2,
    //   render: (alert) => (
    //     <Typography
    //       variant="body2"
    //       sx={{
    //         overflow: 'hidden',
    //         textOverflow: 'ellipsis',
    //         whiteSpace: 'nowrap',
    //       }}
    //     >
    //       {alert.action_taken}
    //     </Typography>
    //   ),
    // },
      {
          key: 'status',
          header: 'Status',
          minWidth: 100,
          noWrap: true,
          align: 'center',
          priority: 2,
          sortable: true,
          sortValue: (alert) => alert.status || '',
          render: (alert) => (
              <Chip
                  label={alert.status?.replace('_', ' ') || 'new'}
                  size="small"
                  sx={{
                      height: 22,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      fontFamily: '"JetBrains Mono", monospace',
                      backgroundColor: 'transparent',
                      color: theme.palette.text.secondary,
                      border: `1px solid ${theme.palette.border.main}`,
                      '& .MuiChip-label': { px: 1 },
                  }}
              />
          ),
      },
    {
      key: 'integration_type',
      header: 'Source',
      minWidth: 100,
      noWrap: true,
      align: 'left',
      priority: 2,
      sortable: true,
      sortValue: (alert) => alert.integration_type || '',
      render: (alert) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            textTransform: 'capitalize',
          }}
        >
          {alert.integration_type || 'N/A'}
        </Typography>
      ),
    },
    {
      key: 'detected_at',
      header: 'Detected',
      minWidth: 100,
      noWrap: true,
      align: 'left',
      priority: 3,
      sortable: true,
      sortValue: (alert) => alert.detected_at ?? '',
      render: (alert) => (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.85rem',
            whiteSpace: 'nowrap',
          }}
        >
          {alert.detected_at ? new Date(alert.detected_at).toLocaleDateString() : 'N/A'}
        </Typography>
      ),
    },
    // {
    //   key: 'created_at',
    //   header: 'Created',
    //   minWidth: 100,
    //   noWrap: true,
    //   priority: 3,
    //   render: (alert) => (
    //     <Typography
    //       variant="body2"
    //       sx={{
    //         color: theme.palette.text.secondary,
    //         fontSize: '0.85rem',
    //         whiteSpace: 'nowrap',
    //       }}
    //     >
    //       {new Date(alert.created_at).toLocaleDateString()}
    //     </Typography>
    //   ),
    // },
  ];

  // Filter columns based on available space
  const columns: Column<Alert>[] = useMemo(() => {
    return allColumns.filter(col => {
      if (col.priority === 1) return true; // Always show priority 1
      if (col.priority === 2) return showMediumColumns; // Show priority 2 when medium space
      if (col.priority === 3) return showAllColumns; // Show priority 3 only with full space
      return true;
    });
  }, [showAllColumns, showMediumColumns]);

  return (
    <Box sx={{
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      px: { xs: 2, sm: 3, md: 4 },
      pt: 10, pb: 3,
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '100%',
      }}>
        {/* Header Section */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 4,
        }}>
          <Box>
            <Typography variant="h1" sx={{ color: theme.palette.text.primary, fontWeight: 600, marginBottom: 0.5 }}>
              Alerts
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Monitor and manage security alerts
            </Typography>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper sx={{
          mb: 3,
          pr: 2,
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
        }}>
          <Grid container spacing={3} alignItems='flex-end'>

            {/* Search Area */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.tertiary,
                    fontWeight: 700,
                    display: 'block',
                    mb: 1,
                    ml: 0.5,
                    letterSpacing: '0.05em'
                  }}
                >
                  Search
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Search alerts..."
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.text.tertiary, fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.tertiary,
                      height: 40,
                      '& fieldset': { border: 'none' },
                      '&:hover': { backgroundColor: theme.palette.background.secondary },
                      '&.Mui-focused': {
                        backgroundColor: theme.palette.background.default,
                        boxShadow: `0 0 0 1px ${theme.palette.text.primary}`,
                      }
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Dropdowns Area */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}>
                {/* Category Filter (maps to integration_type) */}
                <Box sx={{ minWidth: 140 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.tertiary,
                      fontWeight: 700,
                      display: 'block',
                      mb: 1,
                      ml: 0.5,
                      letterSpacing: '0.05em'
                    }}
                  >
                    Category
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: 40,
                        fontSize: '0.8rem',
                        backgroundColor: theme.palette.background.tertiary,
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover': { backgroundColor: theme.palette.background.secondary },
                        '&.Mui-focused': {
                          backgroundColor: theme.palette.background.default,
                          boxShadow: `0 0 0 1px ${theme.palette.text.primary}`
                        }
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                        All
                      </MenuItem>
                      {integrationTypes.map((type) => (
                        <MenuItem key={type} value={type} sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Status Filter */}
                <Box sx={{ minWidth: 140 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.tertiary,
                      fontWeight: 700,
                      display: 'block',
                      mb: 1,
                      ml: 0.5,
                      letterSpacing: '0.05em'
                    }}
                  >
                    Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: 40,
                        fontSize: '0.8rem',
                        backgroundColor: theme.palette.background.tertiary,
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover': { backgroundColor: theme.palette.background.secondary },
                        '&.Mui-focused': {
                          backgroundColor: theme.palette.background.default,
                          boxShadow: `0 0 0 1px ${theme.palette.text.primary}`
                        }
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                        All
                      </MenuItem>
                      {alertStatuses.map((status) => (
                        <MenuItem key={status} value={status} sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>
                          {status.replace('_', ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Risk Level Filter */}
                <Box sx={{ minWidth: 140 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.tertiary,
                      fontWeight: 700,
                      display: 'block',
                      mb: 1,
                      ml: 0.5,
                      letterSpacing: '0.05em'
                    }}
                  >
                    Risk Level
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={severityFilter}
                      onChange={(e) => setSeverityFilter(e.target.value)}
                      displayEmpty
                      sx={{
                        height: 40,
                        fontSize: '0.8rem',
                        backgroundColor: theme.palette.background.tertiary,
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '&:hover': { backgroundColor: theme.palette.background.secondary },
                        '&.Mui-focused': {
                          backgroundColor: theme.palette.background.default,
                          boxShadow: `0 0 0 1px ${theme.palette.text.primary}`
                        }
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.8rem', color: theme.palette.text.tertiary }}>
                        All
                      </MenuItem>
                      <MenuItem value="Critical" sx={{ fontSize: '0.8rem' }}>Critical</MenuItem>
                      <MenuItem value="High" sx={{ fontSize: '0.8rem' }}>High</MenuItem>
                      <MenuItem value="Medium" sx={{ fontSize: '0.8rem' }}>Medium</MenuItem>
                      <MenuItem value="Low" sx={{ fontSize: '0.8rem' }}>Low</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 0.25 }}>
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      bgcolor: theme.palette.background.tertiary,
                      color: theme.palette.info.main,
                      borderRadius: 1,
                      width: 40,
                      height: 40,
                      '&:hover': {
                        bgcolor: theme.palette.info.main,
                        color: theme.palette.background.tertiary,
                        transform: 'rotate(180deg)',
                      },
                      transition: 'all 0.4s ease',
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Data Table */}
        <DataTable
          columns={columns}
          data={filteredAlerts}
          rowKey={(alert) => alert.id}
          isLoading={isLoading}
          error={error}
          emptyMessage="No alerts found"
          emptySubMessage={searchTerm ? 'Try adjusting your search terms' : 'No alerts have been created yet'}
          loadingMessage="Loading alerts..."
          onRowClick={handleRowClick}
          onRowContextMenu={handleRowContextMenu}
          onRetry={handleRefresh}
          minTableWidth={400}
          defaultRowsPerPage={25}
          activeRowKey={selectedAlert?.id ?? null}
        />

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.border.main}`,
              borderRadius: 1.5,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              minWidth: 180,
            },
          }}
        >
          <MenuItem onClick={handleCreateTicketFromContext} sx={{ gap: 1.5 }}>
            <ListItemIcon sx={{ color: theme.palette.text.secondary, minWidth: 'auto' }}>
              <TicketIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Create Ticket"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
            />
          </MenuItem>
        </Menu>

        {/* Create Ticket from Alert Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={handleCreateDialogClose}
          maxWidth="md"
          fullWidth
          disableScrollLock
          PaperProps={{
            sx: {
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.border.main}`,
              borderRadius: 2,
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <DialogTitle sx={{
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.border.light}`,
            fontWeight: 700,
          }}>
            Create Ticket from Alert
          </DialogTitle>
          <DialogContent>
            <NewTicketForm
              onSuccess={handleCreateDialogClose}
              initialData={alertForTicket ? {
                alert_ids: [alertForTicket.id],
                title: alertForTicket.title,
                description: alertForTicket.description,
                client_name: alertForTicket.client_name,
                client_id: alertForTicket.client_id,
                priority: mapSeverityToPriority(alertForTicket.severity),
                riskScore: alertForTicket.risk_score,
                raw_data: alertForTicket.raw_data,
              } : undefined}
            />
          </DialogContent>
          <DialogActions sx={{
            borderTop: `1px solid ${theme.palette.border.light}`,
            p: 2,
          }}>
            <Button
              onClick={handleCreateDialogClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.interactive.hover,
                },
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default AlertsPage;
